import { rem } from 'polished'
import { css } from 'styled-components'

export const COMPACT_SEARCH_TILE_IMAGE_COLUMN_WIDTH = 136
export const COMPACT_SEARCH_TILE_IMAGE_CONTENT_GAP = 16
export const COMPACT_SEARCH_TILE_ROW_PADDING_INLINE_START = 16
export const COMPACT_SEARCH_TILE_ROW_PADDING_INLINE_END = 24
export const COMPACT_SEARCH_TILE_ROW_PADDING_BLOCK = 16
/** Mosaic gutter between main image and thumb row (and between thumbs). */
export const COMPACT_SEARCH_TILE_MOSAIC_GUTTER = 4
export const COMPACT_SEARCH_TILE_MOSAIC_THUMB_SIZE =
  (COMPACT_SEARCH_TILE_IMAGE_COLUMN_WIDTH - COMPACT_SEARCH_TILE_MOSAIC_GUTTER) / 2
/** Main column + thumb row + gutter — keep skeleton hero in sync to avoid CLS. */
export const COMPACT_SEARCH_TILE_MOSAIC_MIN_HEIGHT =
  COMPACT_SEARCH_TILE_IMAGE_COLUMN_WIDTH +
  COMPACT_SEARCH_TILE_MOSAIC_THUMB_SIZE +
  COMPACT_SEARCH_TILE_MOSAIC_GUTTER

export const searchTileContainerQueryUp = {
  tablet: `@container search-tile (width >= ${rem(578)})`,
  desktopSmall: `@container search-tile (width >= ${rem(670)})`,
  desktop: `@container search-tile (width >= ${rem(890)})`,
}

export const metaSearchTileContainerQueryUp = {
  ...searchTileContainerQueryUp,
  desktopLarge: `@container search-tile (width >= ${rem(1100)})`,
}

/** CRO-6924: image and content sit side by side in a fixed-width row instead of stacking. */
const croCompactRowLayout = css`
  min-height: 0;
  grid-template:
    "hero content" auto / ${rem(COMPACT_SEARCH_TILE_IMAGE_COLUMN_WIDTH)} minmax(0, 1fr);
  column-gap: ${rem(COMPACT_SEARCH_TILE_IMAGE_CONTENT_GAP)};
  padding-block: ${rem(COMPACT_SEARCH_TILE_ROW_PADDING_BLOCK)};
  padding-inline: ${rem(COMPACT_SEARCH_TILE_ROW_PADDING_INLINE_START)} ${rem(COMPACT_SEARCH_TILE_ROW_PADDING_INLINE_END)};
  align-items: stretch;
`

const croCompactRowColumns = css`
  grid-template:
    "hero content" auto / ${rem(COMPACT_SEARCH_TILE_IMAGE_COLUMN_WIDTH)} minmax(0, 1fr);
  align-items: stretch;
`

const compactVerticalLayout = css`
  min-height: ${rem(394)};
  grid-template:
    "hero" ${rem(186)}
    "content" auto / 1fr;
`

/** Shared layout styles — density/treatment/fitContent via className only. */
export function searchTileLayoutStyles(defaultMinHeight: string) {
  return css`
    display: grid;
    min-height: ${defaultMinHeight};
    grid-template:
      "hero" ${rem(172)}
      "content" auto / 1fr;

    &.density-compact {
      ${compactVerticalLayout}
    }

    &.density-cro-compact {
      ${croCompactRowLayout}
    }

    &.fit-content {
      min-height: auto;
    }

    ${searchTileContainerQueryUp.tablet} {
      min-height: ${defaultMinHeight};
      grid-template:
        "hero" ${rem(200)}
        "content" auto / 1fr;

      &.density-compact {
        min-height: ${rem(408)};
      }

      &.density-cro-compact {
        ${croCompactRowLayout}
      }

      &.fit-content {
        min-height: auto;
      }
    }

    ${searchTileContainerQueryUp.desktopSmall} {
      grid-template: "hero content" auto / ${rem(300)} minmax(0, 1fr);

      &.density-compact {
        grid-template:
          "hero content" auto / ${rem(260)} minmax(0, 1fr);
      }

      &.density-cro-compact {
        ${croCompactRowColumns}
      }
    }

    ${searchTileContainerQueryUp.desktop} {
      grid-template: "hero content" auto / ${rem(410)} minmax(0, 1fr);

      &.density-compact {
        grid-template:
          "hero content" auto / ${rem(360)} minmax(0, 1fr);
      }

      &.density-cro-compact {
        ${croCompactRowColumns}
      }
    }
  `
}

export const searchTileContentSpacingStyles = css`
  gap: ${rem(16)};
  padding: ${rem(16)};

  &.density-compact {
    gap: ${rem(12)};
    padding: ${rem(12)};
  }

  &.density-cro-compact {
    gap: ${rem(8)};
    padding: 0;
  }

  ${searchTileContainerQueryUp.desktopSmall} {
    padding: ${rem(20)};

    &.density-compact {
      padding: ${rem(16)};
    }

    &.density-cro-compact {
      padding: 0;
    }
  }
`

const featuredMatchBorderRule = css`
  border-radius: ${props => props.theme.borderRadius.S};
  border: 2px solid ${props => props.theme.palette.highlight.primary.normalBackground};
  overflow: hidden;
`

export const searchTileFeaturedMatchBorderStyles = css`
  &.treatment-featured-match {
    ${featuredMatchBorderRule}
  }
`

export const searchTileResponsiveStateBorderStyles = css`
  ${searchTileContainerQueryUp.tablet} {
    &:not(.density-cro-compact):not(.treatment-featured-match) {
      border-radius: ${props => props.theme.borderRadius.S};
      border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

      &.state-default {
        border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
      }
      &.state-highlighted {
        border: 1px solid ${props => props.theme.palette.highlight.primary.darkForeground};
      }
    }

    &:not(.density-cro-compact) {
      ${searchTileFeaturedMatchBorderStyles}
    }
  }
`
